/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flatten from 'lodash/flatten';
import * as React from 'react';
import { useTokenRects } from 'src/modules/labeling/hooks/layout/useTokenRects';
import { MultilineLabel } from 'src/modules/labeling/types/Label';
import { calcLinesHeights, calculateLineIndices, convertToMultiline } from 'src/modules/labeling/utils/layout';
import { RenderPhase, RenderPhaseAction } from 'src/modules/labeling/utils/renderPhaseReducer';

type CalcLayoutInput<T> = {
    /**
     * Array of lines of utterance tokens.
     */
    tokens: string[];

    /**
     * Splitter element ref.
     */
    containerRef: React.MutableRefObject<T>;

    /**
     * Array of labels.
     */
    labels: MultilineLabel[];

    /**
     * The absolute start token index of the line relative
     * to the other lines.
     */
    absoluteStartTokenIndex: number;

    /**
     * When toggled, we need to recalculate layout.
     */
    hideNames: boolean;

    /**
     * Which state in the rendering are we in.
     */
    renderPhase: RenderPhase;

    /**
     * Dispatches against the rendering phase reducer.
     */
    dispatchRenderPhase: (action: RenderPhaseAction) => void;
};

/**
 * This algorithm facilitates rendering shapes on top of the tokens by doing the following:
 *
 * 1) Get the maximum prediction and label levels.
 * 2) Calculate tokens rects.
 * 3) Using tokens rects, calculate line indices.
 * 4) Using line indices, convert labels to multiline ones.
 */
export const useCalculateLineLayout = <T extends HTMLElement>(input: CalcLayoutInput<T>) => {
    const { containerRef, labels, tokens, hideNames, renderPhase, dispatchRenderPhase, absoluteStartTokenIndex } = input;

    const tokenRects = useTokenRects({
        containerRef,
        tokens,
        hideNames,
        labels,
        absoluteStartTokenIndex,
        renderPhase,
        dispatchRenderPhase
    });
    const lineIndices = React.useMemo(() => calculateLineIndices(tokenRects, absoluteStartTokenIndex), [
        tokenRects,
        absoluteStartTokenIndex
    ]);
    const multilineLabels = React.useMemo(() => flatten(convertToMultiline(labels, lineIndices)), [labels, lineIndices]);
    const maxLevelHeights = React.useMemo(() => calcLinesHeights({ multilineLabels, lineIndices }), [multilineLabels, lineIndices]);

    return { multilineLabels, tokenRects, maxLevelHeights, lineIndices };
};
